import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/utils/cn'

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl'

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm:   'max-w-sm',
  md:   'max-w-md',
  lg:   'max-w-lg',
  xl:   'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
}

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  /** Clicking the backdrop closes the modal unless this is true */
  disableBackdropClose?: boolean
  size?: ModalSize
  children: ReactNode
  footer?: ReactNode
  className?: string
}

/** Duration must match the CSS animation duration (ms) */
const EXIT_DURATION = 150

export function Modal({
  open,
  onClose,
  title,
  description,
  disableBackdropClose = false,
  size = 'md',
  children,
  footer,
  className,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState(false)
  const [exiting, setExiting] = useState(false)

  // ── Mount/unmount with animation ─────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setMounted(true)
      setExiting(false)
    } else if (mounted) {
      setExiting(true)
      const timer = setTimeout(() => {
        setMounted(false)
        setExiting(false)
      }, EXIT_DURATION)
      return () => clearTimeout(timer)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // ── Escape key ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mounted) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [mounted, onClose])

  // ── Body scroll lock ─────────────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = mounted ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [mounted])

  if (!mounted) return null

  return createPortal(
    <div
      role="dialog"
      aria-modal
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-describedby={description ? 'modal-desc' : undefined}
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div
        className={cn(
          'absolute inset-0 bg-black/50 backdrop-blur-[2px]',
          exiting ? 'animate-backdrop-out' : 'animate-backdrop-in'
        )}
        onClick={disableBackdropClose ? undefined : onClose}
        aria-hidden
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={cn(
          'relative z-10 flex max-h-[92vh] w-full flex-col rounded-2xl bg-surface shadow-xl',
          'border border-border/60',
          exiting ? 'animate-modal-out' : 'animate-modal-in',
          SIZE_CLASSES[size],
          className
        )}
      >
        {/* Accent line at top */}
        <div className="h-0.5 w-full shrink-0 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />

        {/* Header — fixed, never scrolls */}
        {title && (
          <div className="flex shrink-0 items-start justify-between px-6 pt-5 pb-4">
            <div>
              <h2 id="modal-title" className="text-base font-semibold text-muted-fg">
                {title}
              </h2>
              {description && (
                <p id="modal-desc" className="mt-0.5 text-sm text-muted">
                  {description}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="ms-4 mt-0.5 shrink-0 rounded-lg p-1.5 text-muted transition-colors hover:bg-surface-overlay hover:text-muted-fg"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Body — scrollable */}
        <div className={cn('min-h-0 flex-1 overflow-y-auto px-6 pb-5', !title && 'pt-5')}>
          {children}
        </div>

        {/* Footer — fixed, never scrolls */}
        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border/60 bg-surface-raised/50 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
